import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';

import { withRouter, Link } from 'react-router-dom';

import LeadClientReportNotes from './show/client_report_notes';
import LeadInfo from './show/info';
import EditContactLink from './show/edit_contact_link';
import PVPActivity from './show/pvp_activity';
import LoadingSpinner from 'components/loading_spinner';
import { getProject } from '../../selectors';
import NotifyLead from './show/notify_lead';
import ShareLinkByEmailModal from 'components/documents/share_link_by_email_modal';
import LeadReferralSourceSelect from './show/lead_referral_source_select';
import LeadStatusSelect from './show/lead_status_select';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import DocumentProgress from 'bundles/leads/index/table/cells/document_progress/index';
import {
  DOC_ACCESS_LEVEL_FOR_LEASE,
  DOC_ACCESS_LEVEL_FOR_SALE
} from 'bundles/leads/index/table/cell';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { fetchLeads, removeLead } from 'bundles/project/actions/index';
import GatesProgressBar from './show/gates_progress/bar';
import { refreshLead } from '../../actions/leads';
import { Button, Card, Col, Row } from 'react-bootstrap-5';

const SHARE_DOC_MODAL_DESCRIPTION = 'Easily invite your lead to view your locked documents.';
const WITH_GATES =
  ` Once sent, the invitee will adhere to the CA & Access settings you've configured for your documents.`;
const WITHOUT_GATES = ' The invitee will skip the waitlist.';

export function LeadShow({
  lead,
  project,
  isFetchingLeads,
  features,
  fetchLeads,
  removeLead,
  refreshLead,
  useVaultGates
}) {
  const [showShareByEmailModal, setShowShareByEmailModal] = useState(false);

  const openShareByEmailModal = () => setShowShareByEmailModal(true);
  const hideShareByEmailModal = () => {
    setShowShareByEmailModal(false);
    refreshLead(lead);
  };

  useEffect(() => {
    if (!lead && isFetchingLeads) {
      // this only occurs when navigating directly to a lead by URL
      // leads are fetched on mount in bundles/project/components/leads/list, which hasnt mounted yet
      fetchLeads(project.id);
    }
  }, [lead, isFetchingLeads]);

  if (isFetchingLeads) return <LoadingSpinner addBottomBuffer/>;

  if (!lead) {
    return (
      <div className="mt-5">
        Sorry, we couldn't find this lead.
      </div>
    );
  }

  const leadInfo = {
    accountNotVerified: lead.accountStatus === 'Not Verified',
    caNotSigned: !lead.signedCa,
    pendingApproval: lead.approvalStatus && !['approved', 'auto_approved'].includes(lead.approvalStatus), // waitlisted
    profileNotComplete: !(lead.profileId && lead.profileUserType),
    viewDocsPrompt: !lead.documentsViewed,
    waitlistApproved: ['approved', 'auto_approved'].includes(lead.approvalStatus)
  };

  const renderEditableFields = () => {
    return (
      <div className="form-vertical-field-container">
        <div className="form-group" key="lead-pi-status" name="lead-pi-status">
          <label className="form-label">Lead Status</label>
          <LeadStatusSelect lead={lead} />
        </div>
        <div className="form-group" key="lead-pi-referral-source" name="lead-pi-referral-source">
          <label className="form-label">Referral Source</label>
          <LeadReferralSourceSelect lead={lead} />
        </div>
        {project.leaseChecked && lead.listingLabel &&
          <div className="form-group" key="lead-pi-space" name="lead-pi-space">
            <label className="form-label">Space(s)</label>
            <p>{lead.listingLabel}</p>
          </div>
        }
        {renderDocsAccessLevelDropdowns()}
      </div>
    );
  };

  const renderDocsAccessLevelDropdowns = () => {
    const SelectOption = project.useVaultGates ? DocumentProgress : DocAccessLevelSelect;

    const dropdownForSale = (project.saleChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-sale" name="lead-pi-doc-access-level-for-sale">
        <label className="form-label">Doc Access Level for Sale</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_SALE} lead={lead} />
      </div>
    );

    const dropdownForLease = (project.leaseChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-lease" name="lead-pi-doc-access-level-for-lease">
        <label className="form-label">Doc Access Level for Lease</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_LEASE} lead={lead} />
      </div>
    );

    return (
      <React.Fragment>
        {dropdownForSale}
        {dropdownForLease}
      </React.Fragment>
    );
  };

  const requestMoreInformationNotesSection = () => {
    if (lead.requestMoreInformationNotes.length === 0) return null;

    return (
      <div className="form-group" key="lead-pi-request-more-info-notes" name="lead-pi-request-more-info-notes">
        <label className="form-control">Request More Information (From Plugin)</label>
        <div className="table-container">
          <table>
            <tbody>
              {lead.requestMoreInformationNotes.map((note, index) => (
                <tr key={index}>
                  <td>{note.createdAt}</td>
                  <td>{note.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const newLeadDetails = () => {
    return (
      <div className="space-above">
        <NotifyLead
          lead={lead}
          leadInfo={leadInfo}
        />
        {lead.propertyVaultProfiles.map(pvp => (
          <GatesProgressBar
            caSigned={!!pvp.caSigned}
            key={`${pvp.id}-${pvp.saleOrLease}`}
            lead={lead}
            leadInfo={leadInfo}
            saleOrLease={pvp.saleOrLease}
            showSaleOrLease={lead.propertyVaultProfiles.length > 1}
            userType={lead.profileUserType}
          />
        ))}
        {renderEditableFields()}
        {features.clientReport && <LeadClientReportNotes className="mt-4" lead={lead} />}
        {requestMoreInformationNotesSection()}
        <PVPActivity lead={lead} />
        <div className="mt-5">
          <Link name="delete-lead" to="/project_leads">
            <Button variant="danger" onClick={() => removeLead(project.id, lead)}>
              <FontAwesomeIcon icon={faTrash} />
              Delete Lead
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="lead-show-page">
        <div className="mb-2">
          <Link name="lead-show-back-link" to="/project_leads">
            <FontAwesomeIcon className="me-2" icon={faChevronLeft}/>
            Back to Leads
          </Link>
        </div>
        <Row>
          <Col><h5>{lead.displayName}</h5></Col>
          <Col xs="auto">
            <Button variant="ghost" onClick={openShareByEmailModal}>
              <FontAwesomeIcon icon={faPaperPlane} />
              Invite to view
            </Button>
          </Col>
          <Col xs="auto">
            <EditContactLink lead={lead}/>
          </Col>
        </Row>
        <Card className="mt-4">
          <Row className="align-items-stretch">
            <Col className="border-end" lg={3} md={4}>
              <Card.Body>
                <LeadInfo lead={lead}/>
                <EditContactLink lead={lead}/>
              </Card.Body>
            </Col>
            <Col>
              <Card.Body>
                {newLeadDetails()}
              </Card.Body>
            </Col>
          </Row>
        </Card>

        <ShareLinkByEmailModal
          description={`${SHARE_DOC_MODAL_DESCRIPTION}${useVaultGates ? WITH_GATES : WITHOUT_GATES}`}
          email={lead.leadData.email}
          fromEmails={project.brokersForShareDocs}
          hideModal={hideShareByEmailModal}
          leadId={lead.id}
          propertyId={project.id}
          show={showShareByEmailModal}
          title="Invite to View documents"
        />
      </div>
    </React.Fragment>
  );
}

LeadShow.propTypes = {
  features: PropTypes.shape({
    clientReport: PropTypes.bool.isRequired,
    projectTasksActivities: PropTypes.bool.isRequired
  }),
  fetchLeads: PropTypes.func.isRequired,
  isFetchingLeads: PropTypes.bool,
  lead: PropTypes.object,
  match: ReactRouterPropTypes.match.isRequired,
  project: PropTypes.object.isRequired,
  refreshLead: PropTypes.func.isRequired,
  removeLead: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired
};

const actions = {
  removeLead,
  refreshLead,
  fetchLeads
};

const mapStateToProps = (state, ownProps) => {
  const leadId = parseInt(ownProps.match.params['id']);

  const isLeadsEmpty = Object.keys(state.leads).length === 0;
  const fetchingState = state.leads.fetching;

  // leads are fetched on mount in bundles/project/components/leads/list, which hasnt mounted yet
  // need to check if leads are being fetched in the scenario of navigating directly to a lead by URL
  // or refreshing the leads show page
  const isFetchingLeads = fetchingState === true || (fetchingState === undefined && isLeadsEmpty);
  const lead = isFetchingLeads ? null : state.leads.find(lead => lead.id === leadId);

  return {
    features: state.features,
    isFetchingLeads: isFetchingLeads,
    lead: lead,
    project: getProject(state),
    useVaultGates: state.project.useVaultGates
  };
};

export default withRouter(connect(mapStateToProps, actions)(LeadShow));
